// ⚠️ AVTOMATIK KO'CHIRILGAN NUSXA — QO'L BILAN TAHRIRLAMANG.
// Manba: frontend/shared/format/format.units.ts
// Yangilash: panel papkasidan `node ../shared/sync.mjs`
// Qorovul: `npm run test:format` (uchala panelda) — nusxa manbadan
// bir bayt farq qilsa yiqiladi.
// Birlik formatlash — bayt, davomiylik, son, bo'sh qiymat.
// Uchala panel (admin, selfcare, portal) uchun BIR XIL.
//
// Backend: trafik — bayt, sessiya vaqti — soniya.

import i18n, { currentIntlLocale } from '@/app/i18n'

import { numberFormat } from './format.intl'

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const

/**
 * Baytni o'qiladigan ko'rinishga, tilga mos: 1610612736 →
 * en "1.5 GB" · ru "1,5 ГБ" · uz "1,5 GB"
 *
 * ⚠️ 1024 lik pog'ona (KiB ma'nosida), 1000 lik EMAS: manba `radacct`
 * oktetlari va tarmoq uskunasi ham shu pog'onada o'lchaydi.
 *
 * Kasr xonalari: bayt uchun 0 (yarim bayt yo'q), 100 dan katta qiymatda 1
 * (jadval ustuni tor), qolganda `digits` (standarti 2).
 */
export function formatBytes(bytes: number | null | undefined, digits = 2): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes) || bytes < 0) return '—'

  const locale = currentIntlLocale()
  if (bytes === 0) {
    return `0 ${i18n.t('units.bytes.B')}`
  }

  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1)
  const value = bytes / Math.pow(1024, i)
  const fractionDigits = i === 0 ? 0 : value >= 100 ? 1 : digits

  const text = numberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(value)

  return `${text} ${i18n.t(`units.bytes.${BYTE_UNITS[i] ?? 'B'}`)}`
}

/**
 * Balans bayti — `formatBytes` dan farqli, MANFIY qiymatni ham ko'rsatadi.
 *
 * Balans manfiy bo'lishi mumkin (postpaid yoki grace): abonent qarzga
 * ishlatgan. `formatBytes` bunday holatda "—" qaytaradi, ya'ni ma'lumot
 * yo'qoladi — shuning uchun bu yerda ishorasi bilan yoziladi.
 */
export function formatBalanceBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—'
  if (bytes < 0) return `-${formatBytes(Math.abs(bytes))}`
  return formatBytes(bytes)
}

const DURATION_SEP_KEY = 'units.duration.sep'

/**
 * Son bilan birlik orasidagi ajratgich — TILDAN olinadi, kodda emas.
 *
 * <h2>⚠️ NEGA QOTIRILGAN BO'SH JOY YARAMAYDI</h2>
 * O'zbekcha va ruscha birliklar SO'Z ("kun", "soat", "daq", "дн", "мин") —
 * ular oldida bo'sh joy SHART, aks holda "20d 4son" kabi chalkash matn
 * chiqadi ("d" inglizchada kun, o'zbekchada daqiqa). Inglizcha birliklar
 * esa BELGI ("d", "h", "min", "s") va o'sha panelning o'zi ularni allaqachon
 * bo'shliqsiz yozadi: `refresh.daysAgo` = "{{count}}d ago",
 * `refresh.every` = "every {{seconds}}s".
 *
 * ⚠️ O'LCHANGAN NUQSON (tester-2, BUG-016 va BUG-002). Bo'shliq kodda
 * qotirilgani uchun inglizcha panelda BIR EKRANDA ikki xil yozuv turardi:
 * kampaniya mukofoti "3 d 0 h 0 min", yonidagi qurilma esa "3d ago".
 * Ajratgichni tilga chiqarish ikkalasini ham bir qoidaga keltiradi va
 * o'zbekcha/ruschani TEGMASDAN qoldiradi.
 *
 * <h2>⚠️ KALIT YO'Q BO'LSA — BO'SH JOY</h2>
 * `selfcare` va `portal` lug'atlarida bu kalit yo'q va i18next bunday
 * holatda KALITNING O'ZINI qaytaradi. Zaxira qiymat aynan shuning uchun:
 * usiz o'sha panellarda "3units.duration.sepkun" chiqardi. Ya'ni bu
 * o'zgarish ularning xulqini umuman o'zgartirmaydi.
 */
function durationSeparator(): string {
  const raw = i18n.t(DURATION_SEP_KEY)
  return raw === DURATION_SEP_KEY ? ' ' : raw
}

/**
 * Soniyani muddatga, tilga mos TUSHUNARLI qisqartmalar bilan:
 * en "2h 14min" · ru "2 ч 14 мин" · uz "2 soat 14 daq"
 *
 * Son va birlik orasidagi ajratgich — {@link durationSeparator}.
 *
 * Eng katta ikki (kun bo'lsa — uch) birlik ko'rsatiladi, qolgani tashlanadi:
 * "3 kun 4 soat 12 daq" dan keyin soniya hech kimga kerak emas.
 */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(totalSeconds)) return '—'
  if (totalSeconds < 0) return '—'

  const s = Math.floor(totalSeconds)
  const days = Math.floor(s / 86400)
  const hours = Math.floor((s % 86400) / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const seconds = s % 60

  const sep = durationSeparator()
  const u = {
    d: `${sep}${i18n.t('units.duration.d')}`,
    h: `${sep}${i18n.t('units.duration.h')}`,
    m: `${sep}${i18n.t('units.duration.m')}`,
    s: `${sep}${i18n.t('units.duration.s')}`,
  }

  if (days > 0) return `${days}${u.d} ${hours}${u.h} ${minutes}${u.m}`
  if (hours > 0) return `${hours}${u.h} ${minutes}${u.m}`
  if (minutes > 0) return `${minutes}${u.m} ${seconds}${u.s}`
  return `${seconds}${u.s}`
}

/** Bo'sh qiymatlar uchun chiziqcha */
export function dash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

/** Raqamni tilga mos ajratib ko'rsatish: en "12,345" · ru "12 345" */
export function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  return numberFormat(currentIntlLocale(), {}).format(n)
}
